import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

import { Observable, Subject, Subscription, combineLatest, map } from 'rxjs';

import { unsubscribe } from '@fcom/core';
import { Location } from '@fcom/core-api';
import { LoaderTheme } from '@fcom/ui-components';

export enum AutocompleteTheme {
  DEFAULT = 'default',
  WEB_2 = 'web-2',
}

type AutocompleteHelperState = { kind: 'loading' } | { kind: 'results'; amount: number } | { kind: 'empty' };

@Component({
  selector: 'fin-locations-autocomplete',
  templateUrl: 'locations-autocomplete.component.html',
  styleUrls: ['./locations-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LocationsAutocompleteComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatAutocompleteTrigger, { static: true }) trigger: MatAutocompleteTrigger;

  @Input()
  parentForm: UntypedFormGroup;

  @Input()
  controlName: string;

  @Input()
  translationPrefix: string;

  @Input()
  inputLabel: string;

  @Input()
  inputType: string;

  @Input()
  placeHolder: string = null;

  @Input()
  options$: Subject<Location[]> = new Subject();

  @Input()
  loading$: Subject<boolean>;

  @Input()
  theme = AutocompleteTheme.DEFAULT;

  readonly LoaderTheme = LoaderTheme;
  readonly AutocompleteTheme = AutocompleteTheme;

  autocompleteHelperState$: Observable<AutocompleteHelperState>;
  isRequired = false;
  optionsAmount$: Observable<number>;

  private subscriptions: Subscription = new Subscription();

  constructor() {
    /* noop */
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.trigger.panelClosingActions.subscribe((e) => {
        if (!e?.source) {
          this.ctrlField.setValue(null);
          this.trigger.closePanel();
        }
      })
    );
  }

  displayLocation(location?: Location): string | undefined {
    return location ? `(${location.locationCode}) ${location.title}` : undefined;
  }

  ngOnInit(): void {
    this.isRequired = this.required as boolean;
    this.optionsAmount$ = this.options$.pipe(map((options) => options.length));
    this.autocompleteHelperState$ = combineLatest([this.loading$, this.optionsAmount$]).pipe(
      map(([loading, optionsAmount]) => {
        if (loading) {
          return { kind: 'loading' } as const;
        }

        return optionsAmount === 0
          ? ({ kind: 'empty' } as const)
          : ({ kind: 'results', amount: optionsAmount } as const);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  get required(): string | boolean | undefined {
    return this.ctrlField.errors?.required;
  }

  get ctrlField(): AbstractControl {
    return this.parentForm.get(this.controlName);
  }
}
